// Core save-state types, as specified by GAME_CREATION_INSTRUCTIONS.md.
// PermanentState survives death. LoopState is recreated when a new loop begins.

export type AbilityId = string
export type FactionId = string
export type LocationId = string
export type RouteId = string
export type UnitId = string
export type EventId = string
export type DialogueId = string
export type BattleId = string

export type AbilityLevel = "none" | "beginner" | "advanced" | "master"

export type FactionTrustLevel =
  | "unknown"
  | "recognized"
  | "trusted"
  | "subjected"

export interface ArmyStack {
  unitId: UnitId
  count: number
}

export interface AntiPlayerPersistentState {
  encounterCount: number
  knownPlayerRoutes: RouteId[]
  knownPlayerAllies: FactionId[]
  failedMethods: string[]
  currentStrategyLevel: number
  identityDiscovered: boolean
  motivationDiscovered: boolean
  /**
   * Where the player has been seen training, so the "attack during training"
   * stage can strike somewhere they actually go rather than somewhere the
   * author guessed. Beyond the shape Phase 6 sketches, which is explicitly a
   * "may include".
   */
  knownTrainingSites: LocationId[]
  /** Times the player has beaten him in the field. Drives how he speaks, not what he does. */
  defeatCount: number
  /** True once he has been neutralized without being killed. Ends his campaign permanently. */
  neutralized: boolean
}

export function createInitialAntiPlayerState(): AntiPlayerPersistentState {
  return {
    encounterCount: 0,
    knownPlayerRoutes: [],
    knownPlayerAllies: [],
    failedMethods: [],
    currentStrategyLevel: 0,
    identityDiscovered: false,
    motivationDiscovered: false,
    knownTrainingSites: [],
    defeatCount: 0,
    neutralized: false,
  }
}

export interface PermanentState {
  /** Which face the player chose at the start (0-based index into the avatar sheet). */
  avatarIndex: number
  abilities: Record<AbilityId, AbilityLevel>
  knowledge: string[]
  discoveredLocations: LocationId[]
  discoveredRoutes: RouteId[]
  spiritProgress: number
  antiPlayerState: AntiPlayerPersistentState
  totalDeaths: number
  longestSurvival: number
  deadlineRevealed: boolean
  /**
   * Which suit of armour the player has bought and kept. 0 is none; each tier
   * up is a better suit (see armorEngine's ARMOR_TIERS). Bought with a loop's
   * looted coin but *permanent* — the one thing that turns per-loop money into
   * lasting power, which is the whole reason coin is worth picking up.
   */
  armorTier: number
  /**
   * Spirit scenes already delivered, by scene id. Permanent, because the
   * spirit's story is told across loops rather than within one — a scene the
   * player has heard must never be re-delivered as though it were new.
   */
  spiritScenesSeen: string[]
  /** Questions already put to the spirit. Their answers stay readable forever. */
  spiritQuestionsAsked: string[]
}

export interface LoopState {
  loopNumber: number
  currentDay: number
  currentLocationId: LocationId
  alive: boolean
  money: number
  factionTrust: Record<FactionId, FactionTrustLevel>
  commandPoints: number
  armiesByLocation: Record<LocationId, ArmyStack[]>
  currentArmy: ArmyStack[]
  worldFlags: string[]
  completedEvents: EventId[]
  defeatedEnemyGroups: string[]
  subjectedFactions: FactionId[]
}

export interface GameState {
  permanent: PermanentState
  loop: LoopState
}
